import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { PAGINATION_OPTIONS } from "../../constants";

interface FileExplorerPaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalFiles: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  allowedPageSizes?: readonly number[];
}

export function FileExplorerPagination({
  currentPage,
  totalPages,
  itemsPerPage,
  totalFiles,
  onPageChange,
  onItemsPerPageChange,
  allowedPageSizes = PAGINATION_OPTIONS,
}: FileExplorerPaginationProps) {
  return (
    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-grid-200 bg-white px-4 py-2 z-10">
      <div className="text-sm text-ink-600">{totalFiles} File(s) found</div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-ink-600">
          <span>Items per page</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 min-w-[80px] justify-between"
              >
                {itemsPerPage}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {allowedPageSizes.map((option) => (
                <DropdownMenuItem
                  key={option}
                  onClick={() => onItemsPerPageChange(option)}
                  className={itemsPerPage === option ? "bg-imperial-50" : ""}
                >
                  {option}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2 text-sm text-ink-600">
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              aria-label="First page"
            >
              &lt;&lt;
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              &lt;
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              &gt;
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage >= totalPages}
              aria-label="Last page"
            >
              &gt;&gt;
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
